// src/context/ProjectContext.js
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { getJSON, setJSON, get, set, del } from '../utils/idbKV';

export const CURRENT_SCHEMA_VERSION = 3;

const clone = obj => JSON.parse(JSON.stringify(obj));

export function makeEmptyProject(name = '') {
    const now = Date.now();
    return {
        name,
        version: CURRENT_SCHEMA_VERSION,
        createdAt: now,
        updatedAt: now,
        info: { caseName: name },
        uvalue: null,
        varmetab: null,
        savings: null,
        reportNote: '',
    };
}

function migrate(p) {
    if (!p || typeof p !== 'object') return p;
    if (p.version === CURRENT_SCHEMA_VERSION) return p;

    const now = Date.now();
    if (!p.createdAt) p.createdAt = now;
    if (!p.updatedAt) p.updatedAt = now;
    if (p.reportNote === undefined) p.reportNote = '';

    p.version = CURRENT_SCHEMA_VERSION;
    return p;
}

const Ctx = createContext(null);
export const useProjects = () => {
    const ctx = useContext(Ctx);
    if (!ctx) throw new Error('useProjects must be inside <ProjectProvider>');
    return ctx;
};

export function ProjectProvider({ children }) {
    const [projects, setProjects] = useState([]);
    const [activeProjectName, setActiveProjectName] = useState('');
    const isLoaded = useRef(false);
    const persistTimer = useRef(null);

    // keeps track of the *last real* (non-empty) project name
    const lastSelectedRef = useRef('');

    // Load projects ONCE at mount (from IndexedDB)
    useEffect(() => {
        if (typeof window === 'undefined') return;

        (async () => {
            try {
                const storedProjects = (await getJSON('projekter', []))?.map(migrate) ?? [];
                const storedActive = await get('activeProject');

                setProjects(storedProjects);

                if (storedActive && storedProjects.some(p => p.name === storedActive)) {
                    setActiveProjectName(storedActive);
                    lastSelectedRef.current = storedActive; // ← remember this
                } else {
                    // If storedActive is invalid, don't select ANY project
                    setActiveProjectName('');
                }
            } catch (err) {
                console.warn('IDB load failed:', err);
                setProjects([]);
                setActiveProjectName('');
            } finally {
                isLoaded.current = true;
            }
        })();
    }, []);

    // Persist projects to IndexedDB with debounce
    useEffect(() => {
        if (!isLoaded.current) return;

        clearTimeout(persistTimer.current);
        persistTimer.current = setTimeout(() => {
            setJSON('projekter', projects).catch(err => {
                console.error('Persist error:', err);
            });
        }, 300);
    }, [projects]);

    // Persist active project immediately on change
    useEffect(() => {
        if (!isLoaded.current) return;

        (async () => {
            try {
                if (activeProjectName) {
                    await set('activeProject', activeProjectName);
                    lastSelectedRef.current = activeProjectName; // ← remember updated
                }
                // if activeProjectName === '' → keep lastSelectedRef
            } catch {
                /* ignore quota/private-mode errors */
            }
        })();
    }, [activeProjectName]);

    // Last-resort persist on unload
    useEffect(() => {
        const flush = () => {
            clearTimeout(persistTimer.current);
            (async () => {
                try {
                    await setJSON('projekter', projects);
                    // fall back to remembered name if current is empty
                    const nameToStore = activeProjectName || lastSelectedRef.current;
                    if (nameToStore) await set('activeProject', nameToStore);
                    else await del('activeProject');
                } catch {
                    /* ignore */
                }
            })();
        };
        window.addEventListener('pagehide', flush);
        return () => window.removeEventListener('pagehide', flush);
    }, [projects, activeProjectName]);

    const activeProject =
        projects.find(p => p.name === activeProjectName) ?? null;

    const saveProject = useCallback(
        (name, bundle = {}) => {
            setProjects(prev => {
                const idx = prev.findIndex(p => p.name === name);
                const now = Date.now();

                if (idx >= 0) {
                    const merged = {
                        ...prev[idx],
                        ...clone(bundle),
                        updatedAt: now,
                    };
                    return prev.map((p, i) =>
                        i === idx ? merged : p
                    );
                }

                const created = {
                    ...makeEmptyProject(name),
                    ...clone(bundle),
                };
                if (!activeProjectName && prev.length === 0)
                    setActiveProjectName(name);
                return [...prev, created];
            });
        },
        [activeProjectName]
    );

    const updateProject = useCallback(
        (name, bundle = {}) => saveProject(name, bundle),
        [saveProject]
    );

    const updateSlice = useCallback((name, key, data) => {
        setProjects(prev => {
            const idx = prev.findIndex(p => p.name === name);
            const now = Date.now();
            if (idx === -1) {
                return [
                    ...prev,
                    {
                        ...makeEmptyProject(name),
                        [key]: clone(data),
                        updatedAt: now,
                    },
                ];
            }
            if (prev[idx][key] === data) return prev;
            const next = [...prev];
            next[idx] = {
                ...prev[idx],
                [key]: clone(data),
                updatedAt: now,
            };
            return next;
        });
    }, []);

    const setSavingsTests = useCallback((name, tests) => {
        if (!name) return;
        setProjects(prev => {
            const idx = prev.findIndex(p => p.name === name);
            if (idx === -1) return prev;
            const next = [...prev];
            next[idx] = {
                ...prev[idx],
                savings: {
                    ...(prev[idx].savings ?? {}),
                    tests: clone(tests),
                },
                updatedAt: Date.now(),
            };
            return next;
        });
    }, []);

    /* ------------------------------------------------------------------
       Update the project-level “rapport‐note” (for the PDF generator)
       ------------------------------------------------------------------ */
    const setReportNote = useCallback((name, note = '') => {
        if (!name) return;

        setProjects(prev => {
            const idx = prev.findIndex(p => p.name === name);
            if (idx === -1) return prev;

            const next = [...prev];
            next[idx] = {
                ...prev[idx],
                reportNote: note.trim(),
                updatedAt: Date.now(),
            };
            return next;
        });
    }, []);

    const deleteProject = useCallback(
        name => {
            setProjects(prev => {
                const next = prev.filter(p => p.name !== name);
                if (name === activeProjectName)
                    setActiveProjectName(next[0]?.name ?? '');
                return next;
            });
        },
        [activeProjectName]
    );

    return (
        <Ctx.Provider
            value={{
                projects,
                activeProjectName,
                activeProject,
                currentProject: activeProject, // legacy alias

                setActiveProjectName,
                saveProject,
                updateProject,
                updateSlice,
                setSavingsTests,
                setReportNote,
                deleteProject,
            }}
        >
            {children}
        </Ctx.Provider>
    );
}
